import React from 'react';
import { ChevronRight } from 'lucide-react';
import AirbnbCard from './AirbnbCard';
import { useFavorites } from '@/hooks/useFavorites';

interface Tour {
  id: string;
  name: string;
  city: string;
  country: string;
  image?: string;
  style?: string;
  description?: string;
  duration?: string;
  museum_count?: number;
}

interface TourCarouselProps {
  title: string;
  tours: Tour[];
  onTourSelect: (tour: Tour) => void;
  onTitleClick?: () => void;
  isFirstCarousel?: boolean;
}


const TourCarousel: React.FC<TourCarouselProps> = ({
  title,
  tours,
  onTourSelect,
  onTitleClick,
  isFirstCarousel = false
}) => {
  const { toggleFavorite, isFavorite } = useFavorites();

  // Filter to show only one tour per city
  const uniqueCityTours = React.useMemo(() => {
    const cityMap = new Map<string, Tour>();
    tours.forEach(tour => {
      if (!cityMap.has(tour.city)) {
        cityMap.set(tour.city, tour);
      }
    });
    return Array.from(cityMap.values());
  }, [tours]);

  if (!uniqueCityTours.length) {
    return null;
  }

  return (
    <div className="mb-8">
      <div 
        className={`flex items-center justify-between mb-4 px-6 ${
          onTitleClick ? 'cursor-pointer' : ''
        }`}
        onClick={onTitleClick}
      >
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        {onTitleClick && (
          <ChevronRight className="w-5 h-5 text-gray-800" />
        )}
      </div>
      
      <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide px-6">
        {uniqueCityTours.map((tour, index) => (
          <AirbnbCard
            key={`${tour.id}-${index}`}
            id={tour.id}
            image={tour.image}
            title={tour.city}
            subtitle={tour.country}
            priority={isFirstCarousel && index === 0}
            onCardClick={() => onTourSelect(tour)}
            onFavoriteClick={() => toggleFavorite(tour.name)}
            isFavorite={isFavorite(tour.name)}
          />
        ))}
      </div>
    </div>
  );
};

export default TourCarousel;